import { atom, useAtom } from "jotai";

export interface SessionObject {
  session: Session;
  user: User;
}

export interface User {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  emailVerified: boolean;
  image: string;
  createdAt: Date;
  updatedAt: Date;
  role: string;
  jobRole?: string;
  banned: boolean;
  banReason: string;
  banExpires: Date;
  /** Whether two-factor authentication is enabled for this user */
  twoFactorEnabled?: boolean;
}

export interface Session {
  id: string;
  expiresAt: Date;
  token: string;
  createdAt: Date;
  updatedAt: Date;
  ipAddress: string | null;
  userAgent?: string | null;
  userId: string;
  impersonatedBy: null;
  activeOrganizationId: string;
}

const _sessionAtom = atom<SessionObject | null>(null);

const persistSession = (session: SessionObject | null) => {
  if (session) {
    localStorage.setItem('auth:session', JSON.stringify(session));
  } else {
    localStorage.removeItem('auth:session');
  }
}

export const sessionAtom = atom((get) => {
  let session = get(_sessionAtom);
  if (session) {
    return session;
  }
  const sessionString = localStorage.getItem('auth:session');
  if (sessionString) {
    session = JSON.parse(sessionString);
  }
  return session;
}, (_get, set, update: SessionObject | null) => {
  set(_sessionAtom, update);
  persistSession(update);
});

export const currentUserAtom = atom<User | null>((get) => {
  const session = get(sessionAtom);
  return session?.user || null;
});

export const useSessionStore = () => {
  const [session, setSession] = useAtom(sessionAtom);
  return { session, setSession, user: session?.user || null };
};

export const updateCurrentUserAtom = atom(null, (get, set, update: User | null) => {
  const session = get(sessionAtom);
  if (session) {
    set(sessionAtom, {
      ...session, user: {
        ...session.user,
        ...update,
      }
    });
  }
})